import { API_BASE_URL } from '../config/constants';
import { resumeApi } from './resumeApi';
import { uploadService } from './upload.service';

export const resumeUploadService = {
  // New bulk upload method using the dedicated bulk upload API
  uploadResumesBulk: async (jobId, files, onProgress) => {
    try {
      // Validate files first
      const validation = uploadService.validateFiles(files);
      if (!validation.isValid) {
        throw new Error(`File validation failed: ${validation.errors.join(', ')}`);
      }

      onProgress({ 
        step: 1, 
        message: `Starting bulk upload for ${files.length} files...`,
        progress: 0 
      });

      // Prepare metadata for the bulk upload
      const metadata = {
        jobId,
        userId: JSON.parse(localStorage.getItem('userInfo') || '{}').id || 'anonymous',
        uploadType: 'resume_bulk',
        timestamp: new Date().toISOString()
      };

      // Use the bulk upload service
      const uploadResult = await uploadService.bulkUploadResumes(files, metadata);

      onProgress({ 
        step: 2, 
        message: `Processing ${files.length} files...`,
        progress: 100 
      });

      return {
        success: true,
        uploadedFiles: uploadResult.uploaded_files || [],
        failedFiles: uploadResult.failed_files || [],
        totalFiles: files.length,
        batchId: uploadResult.batch_id,
        jobId: uploadResult.upload_job_id
      };

    } catch (error) {
      console.error('Error in uploadResumesBulk:', error);
      throw error;
    }
  },

  // Batch upload — all sizes go through /resumes/upload which saves to DB
  uploadResumesBatch: async (jobId, files, onProgress) => {
    try {
      // Get user info
      const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');
      const userId = userInfo.id || userInfo._id || 'anonymous';
      
      // Get job info for thread ID
      let threadId = null;
      try {
        const jobResponse = await fetch(`${API_BASE_URL}/jobs/${jobId}`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        if (jobResponse.ok) {
          const jobData = await jobResponse.json();
          threadId = jobData.data?.threadId;
        }
      } catch (error) {
        console.warn('Error fetching job details:', error);
      }

      onProgress({ 
        step: 1, 
        message: `Starting batch upload for ${files.length} files...`,
        progress: 0 
      });

      // Use the existing upload API that can handle multiple files
      const uploadResult = await resumeApi.uploadResumesBatch(jobId, files, (progressEvent) => {
        onProgress({ 
          step: 1, 
          message: `Uploading files... ${progressEvent.progress}%`,
          progress: progressEvent.progress 
        });
      });

      onProgress({ 
        step: 2, 
        message: `Processing ${files.length} files...`,
        progress: 100 
      });

      return {
        success: true,
        uploadedFiles: uploadResult.data || [],
        failedFiles: uploadResult.errors || [],
        totalFiles: files.length
      };

    } catch (error) {
      console.error('Error in uploadResumesBatch:', error);
      throw error;
    }
  },

  // Asynchronous upload with progress tracking
  uploadResumesAsync: async (jobId, files, onProgress, onComplete, onError) => {
    try {
      // Get user info
      const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');
      const userId = userInfo.id || userInfo._id || 'anonymous';
      
      // Get job info for thread ID
      let threadId = null;
      try {
        const jobResponse = await fetch(`${API_BASE_URL}/jobs/${jobId}`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        if (jobResponse.ok) {
          const jobData = await jobResponse.json();
          threadId = jobData.data?.threadId;
        }
      } catch (error) {
        console.warn('Error fetching job details:', error);
      }

      // Setup callback URL
      const callbackUrl = `${API_BASE_URL}/upload-callback`;

      // Start upload job
      onProgress({ 
        step: 1, 
        message: `Starting upload for ${files.length} files...`,
        progress: 0 
      });

      const uploadResult = await resumeApi.uploadResumesToStorage(
        jobId, 
        files, 
        userId, 
        threadId, 
        callbackUrl
      );

      if (!uploadResult.upload_job_id) {
        throw new Error('Failed to start upload job');
      }

      onProgress({ 
        step: 2, 
        message: `Upload job started (ID: ${uploadResult.upload_job_id}). Monitoring progress...`,
        progress: 5 
      });

      // Poll for job status
      const jobStatus = await pollUploadJobStatus(uploadResult.upload_job_id, onProgress);

      // Call completion handler
      if (onComplete) {
        onComplete({
          success: true,
          uploadedFiles: jobStatus.uploaded_files || [],
          failedFiles: jobStatus.failed_files || [],
          jobId: uploadResult.upload_job_id,
          batchId: uploadResult.batch_id
        });
      }

      return {
        success: true,
        uploadedFiles: jobStatus.uploaded_files || [],
        failedFiles: jobStatus.failed_files || [],
        jobId: uploadResult.upload_job_id,
        batchId: uploadResult.batch_id
      };

    } catch (error) {
      console.error('Error in uploadResumesAsync:', error);
      
      if (onError) {
        onError(error);
      }
      
      throw error;
    }
  },

  // Poll upload job status
  pollUploadJobStatus: async (jobId, onProgress, maxPollingTime = 300000) => { // 5 minutes
    const startTime = Date.now();
    const pollInterval = 3000; // 3 seconds

    while (Date.now() - startTime < maxPollingTime) {
      try {
        const response = await fetch(`${API_BASE_URL}/upload/status/${jobId}`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });

        if (response.ok) {
          const status = await response.json();
          
          if (status.status === 'completed') {
            onProgress({ 
              step: 3, 
              message: 'Upload completed successfully!',
              progress: 100 
            });
            return status;
          }

          if (status.status === 'failed') {
            throw new Error(status.error || 'Upload job failed');
          }

          // Update progress
          const progress = status.progress?.percentage || 0;
          onProgress({ 
            step: 2, 
            message: `Processing files... ${progress}%`,
            progress: progress 
          });

          // Wait before next poll
          await new Promise(resolve => setTimeout(resolve, pollInterval));
        } else {
          throw new Error('Failed to get upload status');
        }
      } catch (error) {
        console.error('Error polling upload status:', error);
        throw error;
      }
    }

    throw new Error('Upload timeout');
  },

  // Legacy method for backward compatibility
  uploadResumes: async (jobId, files, onProgress) => {
    return await resumeUploadService.uploadResumesBatch(jobId, files, onProgress);
  }
};

// Export the polling function for external use
export const pollUploadJobStatus = resumeUploadService.pollUploadJobStatus;